import {
  Chart,
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  BarController,
  BubbleController,
  DoughnutController,
  LineController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Legend,
  Title,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

import { CONFIG } from "../config.js";
import {
  formatCurrency,
  formatDateShort,
  gregorianToBuddhist,
} from "../utils/formatters.js";
import {
  calculateSummary,
  groupByMonth,
  groupByCategory,
  groupByProject,
} from "../services/dataProcessor.js";

// Register Chart.js components and plugin
Chart.register(
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  BarController,
  BubbleController,
  DoughnutController,
  LineController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Legend,
  Title,
  Tooltip,
  ChartDataLabels,
);

/**
 * Charts Component
 * High-Impact Full-Width Data Visualizations
 */
export class Charts {
  constructor() {
    this.chartInstances = {};
  }

  render() {
    return `
      <div class="charts-container-full">
        
        <!-- Section 1: Monthly Trend -->
        <!-- Section 1: Monthly Trend -->
        <div class="glass-panel chart-panel-lg">
          <div class="section-header-responsive">
            <div>
              <h2 class="section-title" style="margin-bottom: 0.5rem;">📈 ວິເຄາະແນວໂນ້ມການເງິນ</h2>
              <p class="chart-subtitle">ລາຍຮັບ ແລະ ລາຍຈ່າຍ ປະຈຳເດືອນ</p>
            </div>
            <div class="badge-premium">
              LIVE ANALYTICS
            </div>
          </div>
          <div class="chart-canvas-container-lg">
            <canvas id="monthlyTrendChart"></canvas>
          </div>
        </div>

        <!-- Section 2: Distribution Row (2 Columns) -->
        <div class="charts-row-dual">
          
          <div class="glass-panel">
            <div class="chart-mini-header">
               <div class="chart-dot chart-dot-green"></div>
               <h3 class="chart-mini-title">ລາຍຮັບທັງໝົດປະຈຳເດືອນ</h3>
               <span id="incomeTotalValue" class="chart-value-lg text-primary">...</span>
            </div>
            <div class="pie-chart-wrapper" style="position: relative; margin-bottom: 2rem;">
              <canvas id="incomeCategoryChart"></canvas>
            </div>
            <div id="incomeLegend" class="custom-legend"></div>
          </div>

          <div class="glass-panel">
            <div class="chart-mini-header">
               <div class="chart-dot chart-dot-red"></div>
               <h3 class="chart-mini-title">ລາຍຈ່າຍທັງໝົດປະຈຳເດືອນ</h3>
               <span id="expenseTotalValue" class="chart-value-lg text-primary">...</span>
            </div>
            <div class="pie-chart-wrapper" style="position: relative; margin-bottom: 2rem;">
              <canvas id="expenseCategoryChart"></canvas>
            </div>
            <div id="expenseLegend" class="custom-legend"></div>
          </div>

        </div>

        </div>
        
        <div class="glass-panel">
          <h3 class="section-title">📊 ລາຍຮັບ-ລາຍຈ່າຍ</h3>
          <div class="chart-canvas-container-md">
            <canvas id="incomeExpenseChart"></canvas>
          </div>
          </div>
          <div class="glass-panel">
             <h3 class="section-title" style="margin-bottom: 2rem;">🏢 ໂຄງການ</h3>
             <div style="overflow-x: auto; width: 100%; padding-bottom: 1rem;">
                <div id="projectChartContainer" style="height: 320px; position: relative; min-width: 100%;">
                    <canvas id="projectChart"></canvas>
                </div>
             </div>
          </div>

      </div>
    `;
  }

  initialize(data) {
    if (!data || !data.transactions) return;

    this.destroyAllCharts();

    const transactions = data.transactions;
    const summary = data.summary || calculateSummary(transactions);
    const monthlyData = groupByMonth(transactions);

    // Split categories by type
    const incomeTransactions = transactions.filter(
      (t) => t.type === "ລາຍຮັບ" || t.type === "income",
    );
    const expenseTransactions = transactions.filter(
      (t) => t.type === "ລາຍຈ່າຍ" || t.type === "expense",
    );

    const incomeCategoryData = groupByCategory(incomeTransactions);
    const expenseCategoryData = groupByCategory(expenseTransactions);
    const projectData = groupByProject(transactions);

    // Calculate Totals in Kip (applying exchange rate for foreign currencies)
    const kipSummary = calculateSummary(transactions);
    const totalIncomeKip = kipSummary.totalIncome;
    const totalExpenseKip = kipSummary.totalExpense;

    // Check if any transaction uses a foreign currency (for the indicator note)
    const hasForeignCurrency = transactions.some(
      (t) => t.currency && t.currency !== "₭",
    );

    // Update UI
    setTimeout(() => {
      const incomeValue = document.getElementById("incomeTotalValue");
      if (incomeValue) {
        incomeValue.textContent = formatCurrency(totalIncomeKip);
        if (hasForeignCurrency) {
          incomeValue.title = "ຄຳນວນໂດຍໃຊ້ອັດຕາແລກປ່ຽນ (ກີບ)";
          incomeValue.style.cursor = "help";
        }
      }

      const expenseValue = document.getElementById("expenseTotalValue");
      if (expenseValue) {
        expenseValue.textContent = formatCurrency(totalExpenseKip);
        if (hasForeignCurrency) {
          expenseValue.title = "ຄຳນວນໂດຍໃຊ້ອັດຕາແລກປ່ຽນ (ກີບ)";
          expenseValue.style.cursor = "help";
        }
      }

      // Show foreign currency note if needed
      const noteId = "foreignCurrencyNote";
      let noteEl = document.getElementById(noteId);
      if (hasForeignCurrency && !noteEl) {
        noteEl = document.createElement("div");
        noteEl.id = noteId;
        noteEl.style.cssText =
          "text-align:center; font-size:0.78rem; color:var(--text-secondary); margin-bottom:0.5rem; padding: 0.3rem 0.8rem; background:rgba(251,191,36,0.12); border-radius:8px; display:inline-block;";
        noteEl.textContent = "💱 ຍອດລວມຄຳນວນເປັນກີບ (₭) ໂດຍໃຊ້ອັດຕາແລກປ່ຽນ";
        const container = document.querySelector(".charts-row-dual");
        if (container) container.insertAdjacentElement("beforebegin", noteEl);
      }

      this.createMonthlyTrendChart("monthlyTrendChart", monthlyData);
      this.createCategoryChart(
        "incomeCategoryChart",
        "incomeLegend",
        incomeCategoryData,
        "ລາຍຮັບ",
      );
      this.createCategoryChart(
        "expenseCategoryChart",
        "expenseLegend",
        expenseCategoryData,
        "ລາຍຈ່າຍ",
      );
      this.createIncomeExpenseChart("incomeExpenseChart", monthlyData);
      this.createProjectChart("projectChart", projectData);
    }, 300);
  }

  destroyAllCharts() {
    Object.values(this.chartInstances).forEach((chart) => {
      if (chart) chart.destroy();
    });
    this.chartInstances = {};
  }

  createIncomeExpenseChart(canvasId, monthlyData) {
    const ctx = document.getElementById(canvasId);
    if (!ctx) return;

    // Generate labels for all 12 months
    const allMonths = [
      "ມັງກອນ",
      "ກຸມພາ",
      "ມີນາ",
      "ເມສາ",
      "ພຶດສະພາ",
      "ມິຖຸນາ",
      "ກໍລະກົດ",
      "ສີງຫາ",
      "ກັນຍາ",
      "ຕຸລາ",
      "ພະຈິກ",
      "ທັນວາ",
    ];

    // Initialize arrays with 0 for all 12 months
    const incomeData = new Array(12).fill(0);
    const expenseData = new Array(12).fill(0);

    // Map existing data to the corresponding month index
    monthlyData.forEach((d) => {
      // d.month is 0-indexed (0 = Jan, 11 = Dec)
      if (d.month >= 0 && d.month < 12) {
        incomeData[d.month] = d.income;
        expenseData[d.month] = d.expense;
      }
    });

    this.chartInstances[canvasId] = new Chart(ctx, {
      type: "bar",
      data: {
        labels: allMonths,
        datasets: [
          {
            label: "ລາຍຮັບ",
            data: incomeData,
            backgroundColor: "rgba(16, 185, 129, 0.8)", // More vibrant green
            borderColor: "#10b981",
            borderWidth: 2,
            borderRadius: 6,
            barPercentage: 0.6,
            categoryPercentage: 0.8,
          },
          {
            label: "ລາຍຈ່າຍ",
            data: expenseData,
            backgroundColor: "rgba(239, 68, 68, 0.8)", // More vibrant red
            borderColor: "#ef4444",
            borderWidth: 2,
            borderRadius: 6,
            barPercentage: 0.6,
            categoryPercentage: 0.8,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
            align: "center",
            labels: {
              usePointStyle: true,
              pointStyle: "rectRounded",
              font: { family: "Noto Sans Lao", size: 12, weight: "700" },
              color: "#334155",
            },
          },
          tooltip: {
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            titleColor: "#000",
            bodyColor: "#000",
            borderColor: "#e2e8f0",
            borderWidth: 1,
            titleFont: { family: "Noto Sans Lao", size: 14 },
            bodyFont: { family: "Noto Sans Lao", size: 13 },
            padding: 12,
            callbacks: {
              label: (ctx) =>
                ` ${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`,
            },
          },
          datalabels: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: "rgba(0,0,0,0.05)", drawBorder: false },
            ticks: {
              font: { family: "Outfit", size: 11 },
              callback: (value) =>
                new Intl.NumberFormat("en-US", {
                  notation: "compact",
                  compactDisplay: "short",
                }).format(value),
            },
          },
          x: {
            grid: { display: false },
            ticks: {
              font: {
                family: "Noto Sans Lao",
                size: window.innerWidth < 480 ? 9 : 12,
              },
              maxRotation: window.innerWidth < 480 ? 45 : 0,
            },
          },
        },
        interaction: {
          mode: "index",
          intersect: false,
        },
      },
      plugins: [ChartDataLabels],
    });
  }

  createMonthlyTrendChart(canvasId, monthlyData) {
    const ctx = document.getElementById(canvasId);
    if (!ctx) return;

    const labels = monthlyData.map((d) => d.monthName);
    const incomeData = monthlyData.map((d) => d.income);
    const expenseData = monthlyData.map((d) => d.expense);

    this.chartInstances[canvasId] = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "ລາຍຮັບ",
            data: incomeData,
            borderColor: "#10b981",
            backgroundColor: "rgba(16, 185, 129, 0.2)", // More visible fill
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: "#fff",
            pointBorderWidth: 4,
            borderWidth: 6,
          },
          {
            label: "ລາຍຈ່າຍ",
            data: expenseData,
            borderColor: "#ef4444",
            backgroundColor: "rgba(239, 68, 68, 0.2)", // More visible fill
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: "#fff",
            pointBorderWidth: 4,
            borderWidth: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
            align: "end",
            labels: {
              font: {
                size:
                  window.innerWidth < 480
                    ? 11
                    : window.innerWidth < 768
                      ? 13
                      : 16,
                weight: "700",
              },
              padding: window.innerWidth < 768 ? 15 : 30,
            },
          },
          datalabels: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: "rgba(0,0,0,0.05)" },
            ticks: {
              font: {
                size:
                  window.innerWidth < 480
                    ? 10
                    : window.innerWidth < 768
                      ? 11
                      : 14,
                weight: "600",
              },
              callback: (value) =>
                new Intl.NumberFormat("en-US", {
                  notation: "compact",
                  compactDisplay: "short",
                }).format(value),
            },
          },
          x: {
            grid: { display: false },
            ticks: {
              font: {
                size:
                  window.innerWidth < 480
                    ? 9
                    : window.innerWidth < 768
                      ? 11
                      : 15,
                weight: "700",
              },
              maxRotation: window.innerWidth < 480 ? 45 : 0,
            },
          },
        },
      },
      plugins: [ChartDataLabels],
    });
  }

  createCategoryChart(canvasId, legendId, categoryData, type = "ທັງໝົດ") {
    const ctx = document.getElementById(canvasId);
    if (!ctx) return;

    const categories = Object.values(categoryData);
    // Sort by total desc
    categories.sort((a, b) => b.total - a.total);

    const labels = categories.map((c) => c.category);
    const data = categories.map((c) => c.total);
    const totalValue = data.reduce((a, b) => a + b, 0);

    const colors =
      type === "ລາຍຮັບ"
        ? [
            "#10b981", // Green (primary)
            "#3b82f6", // Blue
            "#8b5cf6", // Purple
            "#06b6d4", // Cyan
            "#14b8a6", // Teal
            "#f59e0b", // Amber
            "#ec4899", // Pink
          ]
        : [
            "#ef4444", // Red (primary)
            "#f59e0b", // Amber
            "#8b5cf6", // Purple
            "#ec4899", // Pink
            "#dc2626", // Dark Red
            "#fb7185", // Rose
            "#f87171", // Light Red
          ];

    this.chartInstances[canvasId] = new Chart(ctx, {
      type: "pie",
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: colors,
            borderWidth: 1,
            borderColor: "#fff",
            hoverOffset: 10,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding:
            window.innerWidth < 480 ? 20 : window.innerWidth < 768 ? 40 : 80,
        },
        plugins: {
          legend: {
            display: false,
          },
          datalabels: {
            color: "#334155",
            font: {
              family: "Noto Sans Lao",
              weight: "bold",
              size:
                window.innerWidth < 480 ? 9 : window.innerWidth < 768 ? 11 : 13,
            },
            formatter: (value, ctx) => {
              let sum = 0;
              let dataArr = ctx.chart.data.datasets[0].data;
              dataArr.map((data) => {
                sum += data;
              });
              let percentage = ((value * 100) / sum).toFixed(1) + "%";
              // Only show label if > 3%
              if ((value * 100) / sum < 3) return null;
              return ctx.chart.data.labels[ctx.dataIndex] + "\n" + percentage;
            },
            anchor: "end",
            align: "end",
            offset: 20,
            textAlign: "center",
            clamp: false,
            clip: false,
          },
          tooltip: {
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            titleColor: "#000",
            bodyColor: "#000",
            borderColor: "#eee",
            borderWidth: 1,
            titleFont: { family: "Noto Sans Lao", size: 14 },
            bodyFont: { family: "Noto Sans Lao", size: 13 },
            callbacks: {
              label: (ctx) => ` ${formatCurrency(ctx.parsed)}`,
            },
          },
        },
        animation: { animateRotate: true, animateScale: true },
      },
      plugins: [ChartDataLabels],
    });

    // Generate Custom Legend with Percentages
    const legendContainer = document.getElementById(legendId);
    if (legendContainer) {
      const legendHTML = categories
        .map((cat, index) => {
          const color = colors[index % colors.length];
          const percent =
            totalValue > 0 ? ((cat.total / totalValue) * 100).toFixed(1) : 0;
          return `
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.8rem; padding-bottom: 0.5rem; border-bottom: 1px dashed rgba(0,0,0,0.05);">
            <div style="display: flex; align-items: center; gap: 0.8rem;">
              <span style="width: 12px; height: 12px; border-radius: 50%; background-color: ${color}; display: inline-block;"></span>
              <span style="font-weight: 600; font-size: 0.95rem; color: var(--text-primary);">${cat.category}</span>
            </div>
            <div style="text-align: right;">
              <div style="font-weight: 800; color: var(--text-primary);">${percent}%</div>
              <div style="font-size: 0.8rem; color: var(--text-secondary);">${formatCurrency(cat.total)}</div>
            </div>
          </div>
        `;
        })
        .join("");
      legendContainer.innerHTML = legendHTML;
    }
  }

  createProjectChart(canvasId, projectData) {
    const ctx = document.getElementById(canvasId);
    if (!ctx) return;

    const projects = Object.values(projectData);
    const labels = projects.map((p) => p.project);
    const incomeData = projects.map((p) => p.income);
    const expenseData = projects.map((p) => p.expense);

    // Dynamic Width for Scrolling (100px per project)
    const container = document.getElementById("projectChartContainer");
    if (container) {
      const totalWidth = Math.max(
        container.parentElement.clientWidth,
        projects.length * 100,
      );
      container.style.width = `${totalWidth}px`;
    }

    this.chartInstances[canvasId] = new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "ລາຍຮັບ",
            data: incomeData,
            backgroundColor: "#10b981",
            borderRadius: 4,
          },
          {
            label: "ລາຍຈ່າຍ",
            data: expenseData,
            backgroundColor: "#ef4444",
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { labels: { font: { weight: "700" } } },
          datalabels: { display: false },
        },
        scales: {
          x: {
            ticks: {
              font: {
                size:
                  window.innerWidth < 480
                    ? 10
                    : window.innerWidth < 768
                      ? 12
                      : 14,
                weight: "700",
              },
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              font: { size: window.innerWidth < 480 ? 10 : 12 },
              callback: (value) =>
                new Intl.NumberFormat("en-US", {
                  notation: "compact",
                  compactDisplay: "short",
                }).format(value),
            },
          },
        },
      },
      plugins: [ChartDataLabels],
    });
  }
}
